import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { TaskStatus, scheduleDays, MISSION_TASK_KEY, HIDDEN_MISSION_KEY, CustomTask, buildTaskKey, buildEventKey } from '../data/scheduleData';

export type TeamName = 'black' | 'white';
export type ScoreCategory = 'battle' | 'chemistry' | 'dreamCoins';

export interface TeamScore {
  battle: number;
  chemistry: number;
  dreamCoins: number;
}

export interface ScoreEvent {
  id: string;
  team: TeamName;
  category: ScoreCategory;
  points: number;
  note: string;
  timestamp: number;
  memberId?: string;
  memberName?: string;
}

export interface ScoreState {
  black: TeamScore;
  white: TeamScore;
  history: ScoreEvent[];
  memberScores: Record<string, number>; // 每位成員的累計積分（不受 history 60 筆限制）
}

export interface Member {
  id: string;
  name: string;
  team: TeamName;
  goal: string;
  joinedAt: number;
  avatarUrl?: string;
}

export interface ScheduleStateData {
  eventStatuses: Record<string, TaskStatus>;
  unlockedTasks: Record<string, boolean>;
  secretPairings: Record<string, string>;
  ceremonyActive: boolean;
  ceremonyPhase: 'intro' | 'reveal' | 'done';
  ceremonyIndex: number;
  ceremonyRevealed: boolean;
  ratingActive: boolean;
  votingType: 'team' | 'individual';
  votingTitle: string;
  customTasks: CustomTask[];
  taskOverrides: Record<string, { name: string; desc: string }>;
  eventOverrides: Record<string, { title: string; time: string }>;
  taskAdditions: Record<string, Array<{ id: string; badge: string; badgeColor: string; name: string; desc: string }>>;
  taskDeletions: Record<string, true>;
  settlementActive: boolean;
  hiddenMissionActive: boolean;
  hiddenMissionStatus: 'idle' | 'ongoing' | 'done';
  hiddenMissionStartedAt: number | null;
}

interface ScoreContextValue {
  scores: ScoreState;
  selectedTeam: TeamName | null;
  selectTeam: (team: TeamName) => void;
  addScore: (team: TeamName, category: ScoreCategory, points: number, note: string, memberId?: string, memberName?: string) => Promise<void>;
  resetScores: () => void;
  resetGame: () => Promise<void>;
  resetMembers: () => Promise<void>;
  resetMottos: () => Promise<void>;
  resetEventStatuses: () => Promise<void>;
  resetTaskUnlocks: () => Promise<void>;
  resetLotteryItems: () => Promise<void>;
  resetTaskCustomizations: () => Promise<void>;
  totalBlack: number;
  totalWhite: number;
  showTeamModal: boolean;
  setShowTeamModal: (v: boolean) => void;
  showJudgePanel: boolean;
  setShowJudgePanel: (v: boolean) => void;
  members: Member[];
  currentMember: Member | null;
  addOrUpdateMember: (name: string, goal: string, team: TeamName, avatarUrl?: string) => void;
  updateMember: (id: string, name: string, goal: string) => void;
  updateMemberTeam: (id: string, team: TeamName) => void;
  updateMemberAvatar: (id: string, file: File) => Promise<void>;
  removeMember: (id: string) => void;
  transferMemberRecord: (fromMemberId: string, toMemberId: string) => Promise<void>;
  myMemberId: string | null;
  setMyMemberId: (id: string | null) => void;
  showMemberModal: boolean;
  setShowMemberModal: (v: boolean) => void;
  pendingTeam: TeamName | null;
  setPendingTeam: (team: TeamName | null) => void;
  teamMottos: { black: string; white: string };
  updateTeamMotto: (team: TeamName, motto: string) => void;
  isLoading: boolean;
  scheduleState: ScheduleStateData;
  setEventStatus: (key: string, status: TaskStatus) => void;
  toggleTaskUnlock: (key: string) => void;
  setSecretPairings: (pairings: Record<string, string>) => void;
  setCeremonyActive: (active: boolean) => void;
  setCeremonyProgress: (phase: 'intro' | 'reveal' | 'done', index: number, revealed: boolean) => void;
  setRatingActive: (active: boolean) => void;
  setVotingConfig: (type: 'team' | 'individual', title: string) => void;
  setSettlementActive: (active: boolean) => void;
  setHiddenMissionActive: (active: boolean) => void;
  setHiddenMissionStatus: (status: 'idle' | 'ongoing' | 'done') => void;
  gameStartedAt: number | null;
  startGame: () => Promise<void>;
  addCustomTask: (task: Omit<CustomTask, 'id' | 'createdAt'>) => void;
  removeCustomTask: (id: string) => void;
  setCustomTaskStatus: (id: string, status: TaskStatus) => void;
  updateTaskOverride: (key: string, override: { name: string; desc: string }) => void;
  updateEventOverride: (key: string, override: { title: string; time: string }) => void;
  startEventAndUnlockTasks: (dayId: string, eventIdx: number) => void;
  addTaskToEvent: (dayId: string, eventIdx: number, task: { badge: string; badgeColor: string; name: string; desc: string }) => void;
  deleteTask: (key: string) => void;
  resetAllTasks: () => Promise<void>;
}

// ── Module-level broadcast send ref (set up by ScoreProvider) ─────────────────
let _sysCommentBroadcast: ((id: string, message: string, team: TeamName | null, createdAt: number) => void) | null = null;
// Queue for broadcasts that arrive before channel is subscribed
const _sysBroadcastQueue: Array<[string, string, TeamName | null, number]> = [];

const TEAM_KEY = 'srs-team-v1';
const MY_MEMBER_KEY = 'srs-my-member-v1';

const defaultTeamScore = (): TeamScore => ({ battle: 0, chemistry: 0, dreamCoins: 0 });
const defaultScoreState = (): ScoreState => ({
  black: defaultTeamScore(),
  white: defaultTeamScore(),
  history: [],
  memberScores: {},
});
const defaultScheduleState = (): ScheduleStateData => ({
  eventStatuses: {},
  unlockedTasks: {},
  secretPairings: {},
  ceremonyActive: false,
  ceremonyPhase: 'intro',
  ceremonyIndex: 0,
  ceremonyRevealed: false,
  ratingActive: false,
  votingType: 'individual',
  votingTitle: '',
  customTasks: [],
  taskOverrides: {},
  eventOverrides: {},
  taskAdditions: {},
  taskDeletions: {},
  settlementActive: false,
  hiddenMissionActive: false,
  hiddenMissionStatus: 'idle',
  hiddenMissionStartedAt: null,
});
const getTotal = (t: TeamScore) => t.battle + t.chemistry + t.dreamCoins;

const SCORE_COL: Record<string, string> = {
  'black-battle': 'black_battle',
  'black-chemistry': 'black_chemistry',
  'black-dreamCoins': 'black_dream_coins',
  'white-battle': 'white_battle',
  'white-chemistry': 'white_chemistry',
  'white-dreamCoins': 'white_dream_coins',
};

function dbRowToScoreState(row: Record<string, unknown>): ScoreState {
  return {
    black: {
      battle: (row.black_battle as number) ?? 0,
      chemistry: (row.black_chemistry as number) ?? 0,
      dreamCoins: (row.black_dream_coins as number) ?? 0,
    },
    white: {
      battle: (row.white_battle as number) ?? 0,
      chemistry: (row.white_chemistry as number) ?? 0,
      dreamCoins: (row.white_dream_coins as number) ?? 0,
    },
    history: Array.isArray(row.history) ? (row.history as ScoreEvent[]) : [],
    memberScores: (row.member_scores as Record<string, number>) ?? {},
  };
}

function dbRowToMember(row: Record<string, unknown>): Member {
  return {
    id: row.id as string,
    name: row.name as string,
    team: row.team as TeamName,
    goal: (row.goal as string) ?? '',
    joinedAt: (row.joined_at as number) ?? Date.now(),
    avatarUrl: (row.avatar_url as string) ?? undefined,
  };
}

function dbRowToScheduleState(r: Record<string, unknown>): ScheduleStateData {
  return {
    eventStatuses: (r.event_statuses as Record<string, TaskStatus>) ?? {},
    unlockedTasks: (r.unlocked_tasks as Record<string, boolean>) ?? {},
    secretPairings: (r.secret_pairings as Record<string, string>) ?? {},
    ceremonyActive: (r.ceremony_active as boolean) ?? false,
    ceremonyPhase: (r.ceremony_phase as 'intro' | 'reveal' | 'done') ?? 'intro',
    ceremonyIndex: (r.ceremony_index as number) ?? 0,
    ceremonyRevealed: (r.ceremony_revealed as boolean) ?? false,
    ratingActive: (r.rating_active as boolean) ?? false,
    votingType: (r.voting_type as 'team' | 'individual') ?? 'individual',
    votingTitle: (r.voting_title as string) ?? '',
    customTasks: Array.isArray(r.custom_tasks) ? (r.custom_tasks as CustomTask[]) : [],
    taskOverrides: (r.task_overrides as Record<string, { name: string; desc: string }>) ?? {},
    eventOverrides: (r.event_overrides as Record<string, { title: string; time: string }>) ?? {},
    taskAdditions: (r.task_additions as Record<string, Array<{ id: string; badge: string; badgeColor: string; name: string; desc: string }>>) ?? {},
    taskDeletions: (r.task_deletions as Record<string, true>) ?? {},
    settlementActive: (r.settlement_active as boolean) ?? false,
    hiddenMissionActive: (r.hidden_mission_active as boolean) ?? false,
    hiddenMissionStatus: (r.hidden_mission_status as 'idle' | 'ongoing' | 'done') ?? 'idle',
    hiddenMissionStartedAt: (r.hidden_mission_started_at as number) ?? null,
  };
}

function insertSysComment(message: string, team: TeamName | null = null) {
  const id = `sys-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const createdAt = Date.now();

  // Dispatch locally immediately — Supabase broadcast does NOT self-echo by default,
  // so the sender (judge panel) would otherwise never see the toast.
  window.dispatchEvent(new CustomEvent('srs-notification', {
    detail: {
      id: `comment-${id}`,
      type: 'system',
      displayName: '⚡系統',
      team,
      message,
      timestamp: createdAt,
    },
  }));

  // Fast broadcast path — instant delivery to all other subscribers
  // If channel not ready yet, queue it (will be flushed once SUBSCRIBED)
  if (_sysCommentBroadcast) {
    _sysCommentBroadcast(id, message, team, createdAt);
  } else {
    _sysBroadcastQueue.push([id, message, team, createdAt]);
  }

  // DB insert for persistence / late-joiners
  supabase
    .from('comments')
    .insert({
      id,
      member_id: 'system',
      display_name: '⚡系統',
      team,
      message,
      created_at: createdAt,
    })
    .then(({ error }) => {
      if (error) console.error('[SysComment] Insert failed:', error.message, error);
    });
}

function getEventTitle(key: string): string {
  const lastDash = key.lastIndexOf('-');
  if (lastDash === -1) return key;
  const dayId = key.substring(0, lastDash);
  const eventIdx = parseInt(key.substring(lastDash + 1), 10);
  const day = scheduleDays.find(d => d.id === dayId);
  if (day && day.events[eventIdx]) return day.events[eventIdx].title;
  return key;
}

function getTaskTitle(key: string): string {
  if (key === MISSION_TASK_KEY) return '小天使揭曉儀式';
  const parts = key.split('-');
  if (parts.length < 3) return key;
  const taskIdx = parseInt(parts[parts.length - 1], 10);
  const eventIdx = parseInt(parts[parts.length - 2], 10);
  const dayId = parts.slice(0, parts.length - 2).join('-');
  const day = scheduleDays.find(d => d.id === dayId);
  if (day && !isNaN(eventIdx) && !isNaN(taskIdx) && day.events[eventIdx] && day.events[eventIdx].tasks[taskIdx]) {
    return day.events[eventIdx].tasks[taskIdx].name;
  }
  return key;
}

const ScoreContext = createContext<ScoreContextValue | null>(null);

export function ScoreProvider({ children }: { children: ReactNode }) {
  const [scores, setScores] = useState<ScoreState>(defaultScoreState());
  const [members, setMembers] = useState<Member[]>([]);
  const [teamMottos, setTeamMottos] = useState<{ black: string; white: string }>({ black: '', white: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [gameStartedAt, setGameStartedAt] = useState<number | null>(null);
  const [scheduleState, setScheduleState] = useState<ScheduleStateData>(defaultScheduleState());

  const [selectedTeam, setSelectedTeam] = useState<TeamName | null>(
    () => (localStorage.getItem(TEAM_KEY) as TeamName | null) ?? null,
  );
  const [myMemberId, setMyMemberIdState] = useState<string | null>(
    () => localStorage.getItem(MY_MEMBER_KEY) ?? null,
  );

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showJudgePanel, setShowJudgePanel] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [pendingTeam, setPendingTeam] = useState<TeamName | null>(null);

  const scoresRef = useRef<ScoreState>(defaultScoreState());
  const membersRef = useRef<Member[]>([]);
  const scheduleStateRef = useRef<ScheduleStateData>(defaultScheduleState());
  const suppressRealtimeScore = useRef(false);
  const suppressRealtimeSchedule = useRef(false);
  const gameEventChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const ceremonyChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const settlementChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const hiddenMissionChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const scoreBroadcastRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const scheduleBroadcastRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => { scoresRef.current = scores; }, [scores]);
  useEffect(() => { membersRef.current = members; }, [members]);
  useEffect(() => { scheduleStateRef.current = scheduleState; }, [scheduleState]);

  // ─── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchMembers = useCallback(async () => {
    const { data } = await supabase.from('members').select('*').order('joined_at', { ascending: true });
    if (data) setMembers(data.map(r => dbRowToMember(r as Record<string, unknown>)));
  }, []);

  // ─── Initial Load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setIsLoading(true);
      try {
        const [membersRes, scoresRes, mottosRes, scheduleRes] = await Promise.all([
          supabase.from('members').select('*').order('joined_at', { ascending: true }),
          supabase.from('scores').select('*').eq('id', 'main').maybeSingle(),
          supabase.from('team_mottos').select('*').eq('id', 'main').maybeSingle(),
          supabase.from('schedule_state').select('*').eq('id', 'main').maybeSingle(),
        ]);
        if (cancelled) return;
        if (membersRes.data) setMembers(membersRes.data.map(r => dbRowToMember(r as Record<string, unknown>)));
        if (scoresRes.data) {
          const s = dbRowToScoreState(scoresRes.data as Record<string, unknown>);
          setScores(s); scoresRef.current = s;
          const gsa = (scoresRes.data as Record<string, unknown>).game_started_at;
          setGameStartedAt(typeof gsa === 'number' ? gsa : null);
        }
        if (mottosRes.data) {
          const m = mottosRes.data as Record<string, unknown>;
          setTeamMottos({ black: (m.black as string) ?? '', white: (m.white as string) ?? '' });
        }
        if (scheduleRes.data) {
          const ss = dbRowToScheduleState(scheduleRes.data as Record<string, unknown>);
          setScheduleState(ss);
          scheduleStateRef.current = ss;
        }
      } catch { /* fall back to defaults */ }
      finally { if (!cancelled) setIsLoading(false); }
    }
    loadAll();
    return () => { cancelled = true; };
  }, []);

  // ─── Periodic member sync ──────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(fetchMembers, 15000);
    return () => clearInterval(interval);
  }, [fetchMembers]);

  // ─── Realtime (Postgres Changes) ──────────────────────────────────────────
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const subscribeRealtime = useCallback(() => {
    if (realtimeChannelRef.current) supabase.removeChannel(realtimeChannelRef.current);

    const channel = supabase
      .channel(`srs-realtime-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'members' }, (payload) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        if (eventType === 'INSERT') {
          const m = dbRowToMember(newRow as Record<string, unknown>);
          setMembers(prev => prev.find(x => x.id === m.id) ? prev : [...prev, m]);
          // 不是自己加入 → 跳出系統通知
          const myId = localStorage.getItem(MY_MEMBER_KEY);
          if (m.id !== myId) {
            window.dispatchEvent(new CustomEvent('srs-notification', {
              detail: {
                id: `join-${m.id}`,
                type: 'join',
                displayName: m.name,
                team: m.team,
                timestamp: m.joinedAt,
              },
            }));
          }
        } else if (eventType === 'UPDATE') {
          const m = dbRowToMember(newRow as Record<string, unknown>);
          setMembers(prev => prev.map(x => x.id === m.id ? m : x));
        } else if (eventType === 'DELETE') {
          const deletedId = (oldRow as Record<string, unknown>).id as string;
          setMembers(prev => prev.filter(x => x.id !== deletedId));
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'scores', filter: 'id=eq.main' }, (payload) => {
        if (suppressRealtimeScore.current) return;
        const s = dbRowToScoreState(payload.new as Record<string, unknown>);
        setScores(s); scoresRef.current = s;
        const gsa = (payload.new as Record<string, unknown>).game_started_at;
        setGameStartedAt(typeof gsa === 'number' ? gsa : null);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'team_mottos', filter: 'id=eq.main' }, (payload) => {
        const r = payload.new as Record<string, unknown>;
        setTeamMottos({ black: (r.black as string) ?? '', white: (r.white as string) ?? '' });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'schedule_state', filter: 'id=eq.main' }, (payload) => {
        if (suppressRealtimeSchedule.current) return;
        const ss = dbRowToScheduleState(payload.new as Record<string, unknown>);
        setScheduleState(ss);
        scheduleStateRef.current = ss;
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setTimeout(() => { subscribeRealtime(); fetchMembers(); }, 3000);
        }
      });

    realtimeChannelRef.current = channel;
  }, [fetchMembers]);

  useEffect(() => {
    subscribeRealtime();
    return () => {
      if (realtimeChannelRef.current) { supabase.removeChannel(realtimeChannelRef.current); realtimeChannelRef.current = null; }
    };
  }, [subscribeRealtime]);

  // ─── Re-fetch on visibility change ────────────────────────────────────────
  useEffect(() => {
    const handleActive = async () => {
      if (document.visibilityState !== 'visible') return;

      // 重新訂閱 realtime channel，防止背景時斷線
      subscribeRealtime();

      const [membersRes, scoresRes, mottosRes, scheduleRes] = await Promise.all([
        supabase.from('members').select('*').order('joined_at', { ascending: true }),
        supabase.from('scores').select('*').eq('id', 'main').maybeSingle(),
        supabase.from('team_mottos').select('*').eq('id', 'main').maybeSingle(),
        supabase.from('schedule_state').select('*').eq('id', 'main').maybeSingle(),
      ]);
      if (membersRes.data) setMembers(membersRes.data.map(r => dbRowToMember(r as Record<string, unknown>)));
      if (scoresRes.data && !suppressRealtimeScore.current) {
        const s = dbRowToScoreState(scoresRes.data as Record<string, unknown>);
        setScores(s); scoresRef.current = s;
      }
      if (mottosRes.data) {
        const m = mottosRes.data as Record<string, unknown>;
        setTeamMottos({ black: (m.black as string) ?? '', white: (m.white as string) ?? '' });
      }
      if (scheduleRes.data && !suppressRealtimeSchedule.current) {
        const ss = dbRowToScheduleState(scheduleRes.data as Record<string, unknown>);
        setScheduleState(ss);
        scheduleStateRef.current = ss;
      }
    };
    document.addEventListener('visibilitychange', handleActive);
    window.addEventListener('focus', handleActive);
    return () => {
      document.removeEventListener('visibilitychange', handleActive);
      window.removeEventListener('focus', handleActive);
    };
  }, [subscribeRealtime]);

  // ─── Identity ─────────────────────────────────────────────────────────────
  const setMyMemberId = useCallback((id: string | null) => {
    setMyMemberIdState(id);
    if (id) localStorage.setItem(MY_MEMBER_KEY, id);
    else localStorage.removeItem(MY_MEMBER_KEY);
  }, []);

  const selectTeam = useCallback((team: TeamName) => {
    setSelectedTeam(team);
    localStorage.setItem(TEAM_KEY, team);
    setShowTeamModal(false);
    setPendingTeam(team);
    setShowMemberModal(true);
  }, []);

  // ─── Member Ops ───────────────────────────────────────────────────────────
  const addOrUpdateMember = useCallback(async (name: string, goal: string, team: TeamName, avatarUrl?: string) => {
    const existingId = localStorage.getItem(MY_MEMBER_KEY);
    const memberId = existingId ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const existingInState = membersRef.current.find(m => m.id === memberId);
    const member: Member = {
      id: memberId, name, team, goal,
      joinedAt: existingInState?.joinedAt ?? Date.now(),
      avatarUrl: avatarUrl ?? existingInState?.avatarUrl,
    };
    setMembers(prev => {
      const exists = prev.find(m => m.id === memberId);
      return exists ? prev.map(m => m.id === memberId ? member : m) : [...prev, member];
    });
    if (!existingId) { setMyMemberId(memberId); insertSysComment(`「${name}」加入了${team === 'black' ? '黑隊' : '白隊'}！`, team); }
    await supabase.from('members').upsert({ id: member.id, name: member.name, team: member.team, goal: member.goal, joined_at: member.joinedAt });
    setShowMemberModal(false);
  }, [setMyMemberId]);

  const updateMember = useCallback(async (id: string, name: string, goal: string) => {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, name, goal } : m));
    await supabase.from('members').update({ name, goal }).eq('id', id);
  }, []);

  const updateMemberAvatar = useCallback(async (id: string, file: File) => {
    const ext = file.name.split('.').pop() ?? 'jpg';
    const path = `${id}/avatar.${ext}`;
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type });
    if (uploadError) throw uploadError;
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    setMembers(prev => prev.map(m => m.id === id ? { ...m, avatarUrl } : m));
    await supabase.from('members').update({ avatar_url: avatarUrl }).eq('id', id);
  }, []);

  const updateMemberTeam = useCallback(async (id: string, team: TeamName) => {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, team } : m));
    await supabase.from('members').update({ team }).eq('id', id);
  }, []);

  const removeMember = useCallback(async (id: string) => {
    setMembers(prev => prev.filter(m => m.id !== id));
    const currentId = localStorage.getItem(MY_MEMBER_KEY);
    if (currentId === id) { localStorage.removeItem(MY_MEMBER_KEY); localStorage.removeItem(TEAM_KEY); setMyMemberIdState(null); setSelectedTeam(null); }
    await supabase.from('members').delete().eq('id', id);
  }, []);

  // ─── Transfer Member Record ───────────────────────────────────────────────
  // 把 fromMemberId 的積分紀錄繼承給 toMemberId（合併 memberScores + 更新 history）
  const transferMemberRecord = useCallback(async (fromMemberId: string, toMemberId: string): Promise<void> => {
    const current = scoresRef.current;
    const fromMember = membersRef.current.find(m => m.id === fromMemberId);
    const toMember = membersRef.current.find(m => m.id === toMemberId);
    if (!fromMember || !toMember) throw new Error('找不到成員');

    const fromScore = current.memberScores[fromMemberId] ?? 0;
    const toScore = current.memberScores[toMemberId] ?? 0;

    // 合併積分：to = to + from，清除 from
    const newMemberScores = { ...current.memberScores };
    newMemberScores[toMemberId] = toScore + fromScore;
    delete newMemberScores[fromMemberId];

    // 更新 history：把 fromMemberId 的紀錄改成 toMemberId
    const newHistory = current.history.map(ev => {
      if (ev.memberId === fromMemberId) {
        return { ...ev, memberId: toMemberId, memberName: toMember.name };
      }
      return ev;
    });

    const newState: ScoreState = { ...current, memberScores: newMemberScores, history: newHistory };
    setScores(newState);
    scoresRef.current = newState;

    // 寫入 DB
    suppressRealtimeScore.current = true;
    await supabase.from('scores').update({
      member_scores: newMemberScores,
      history: newHistory,
      updated_at: new Date().toISOString(),
    }).eq('id', 'main');

    // Broadcast 給其他裝置
    scoreBroadcastRef.current?.send({
      type: 'broadcast', event: 'score_update',
      payload: { scores: newState, gameStartedAt },
    });

    setTimeout(() => { suppressRealtimeScore.current = false; }, 1500);

    insertSysComment(
      `📋 裁判已將「${fromMember.name}」的積分紀錄（${fromScore > 0 ? '+' : ''}${fromScore}）繼承給「${toMember.name}」`,
      toMember.team,
    );
  }, [gameStartedAt]);

  // ─── Score Broadcast Channel ──────────────────────────────────────────────
  useEffect(() => {
    const ch = supabase.channel('srs-score-broadcast', { config: { broadcast: { ack: false } } });
    ch
      .on('broadcast', { event: 'score_update' }, (msg) => {
        // 只有裁判端（自己剛寫入）才會設 suppress，玩家端收到 broadcast 直接更新
        // 注意：broadcast 不會 self-echo，所以收到這個訊息的一定是其他裝置
        const { scores: newScores, gameStartedAt: gsa } = msg.payload as { scores: ScoreState; gameStartedAt: number | null };
        setScores(newScores);
        scoresRef.current = newScores;
        if (gsa !== undefined) setGameStartedAt(gsa);
      })
      .subscribe();
    scoreBroadcastRef.current = ch;
    return () => { supabase.removeChannel(ch); scoreBroadcastRef.current = null; };
  }, []);

  // ─── Schedule Broadcast Channel ───────────────────────────────────────────
  useEffect(() => {
    const ch = supabase.channel('srs-schedule-broadcast', { config: { broadcast: { ack: false } } });
    ch
      .on('broadcast', { event: 'schedule_update' }, (msg) => {
        // 只有裁判端（自己剛寫入）才會設 suppress，玩家端收到 broadcast 直接更新
        // 注意：broadcast 不會 self-echo，所以收到這個訊息的一定是其他裝置
        const newState = msg.payload as ScheduleStateData;
        setScheduleState(newState);
        scheduleStateRef.current = newState;
      })
      .subscribe();
    scheduleBroadcastRef.current = ch;
    return () => { supabase.removeChannel(ch); scheduleBroadcastRef.current = null; };
  }, []);

  // ─── Score Ops ────────────────────────────────────────────────────────────
  const addScore = useCallback(async (team: TeamName, category: ScoreCategory, points: number, note: string, memberId?: string, memberName?: string): Promise<void> => {
    const current = scoresRef.current;
    const event: ScoreEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      team, category, points, note, timestamp: Date.now(),
      ...(memberId ? { memberId } : {}), ...(memberName ? { memberName } : {}),
    };

    // Optimistic local update（讓操作者立即看到結果）
    const newValue = Math.max(0, current[team][category] + points);
    const newHistory = [event, ...current.history].slice(0, 60);
    const newMemberScores = memberId
      ? { ...current.memberScores, [memberId]: Math.max(0, (current.memberScores[memberId] ?? 0) + points) }
      : current.memberScores;
    const newState: ScoreState = { ...current, [team]: { ...current[team], [category]: newValue }, history: newHistory, memberScores: newMemberScores };
    setScores(newState); scoresRef.current = newState;

    const catLabel: Record<ScoreCategory, string> = { battle: '戰鬥積分', chemistry: '默契加分', dreamCoins: '圓夢硬幣' };
    const teamCN = team === 'black' ? '黑隊' : '白隊';
    const target = memberName || teamCN;
    const sign = points >= 0 ? '+' : '';
    insertSysComment(`${target} 獲得 ${sign}${points} ${catLabel[category]}${note ? `（${note}）` : ''}`, team);

    // 使用 atomic RPC 寫入 DB，防止多人同時領取時互相覆蓋 history
    suppressRealtimeScore.current = true;
    const { data, error } = await supabase.rpc('append_score_event', {
      p_team: team,
      p_category: category,
      p_points: points,
      p_event: event as unknown as Record<string, unknown>,
    });

    if (error) {
      console.error('[addScore] RPC error:', error.message);
      // RPC 失敗時 fallback 到直接 update
      const col = SCORE_COL[`${team}-${category}`];
      await supabase.from('scores').update({
        [col]: newValue,
        history: newHistory,
        updated_at: new Date().toISOString(),
      } as Record<string, unknown>).eq('id', 'main');
      // fallback 失敗時拋出讓呼叫端知道
      if (error) throw new Error(error.message);
    } else if (data) {
      // 用 DB 回傳的真實值更新本地狀態（確保和 DB 一致）
      const dbState = dbRowToScoreState(data as Record<string, unknown>);
      setScores(dbState); scoresRef.current = dbState;
      // Broadcast DB 真實值給所有其他裝置
      scoreBroadcastRef.current?.send({
        type: 'broadcast', event: 'score_update',
        payload: { scores: dbState, gameStartedAt: gameStartedAt },
      });
    }

    setTimeout(() => { suppressRealtimeScore.current = false; }, 1500);
  }, [gameStartedAt]);

  const resetScores = useCallback(async () => {
    const fresh = defaultScoreState();
    setScores(fresh); scoresRef.current = fresh;
    suppressRealtimeScore.current = true;
    await supabase.from('scores').update({
      black_battle: 0, black_chemistry: 0, black_dream_coins: 0,
      white_battle: 0, white_chemistry: 0, white_dream_coins: 0,
      history: [], game_started_at: null, updated_at: new Date().toISOString(),
    }).eq('id', 'main');
    setTimeout(() => { suppressRealtimeScore.current = false; }, 500);
  }, []);

  // ─── Game Reset Broadcast ─────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase.channel('srs-game-broadcast', { config: { broadcast: { ack: false } } });
    channel
      .on('broadcast', { event: 'game_reset' }, () => {
        localStorage.removeItem(MY_MEMBER_KEY); localStorage.removeItem(TEAM_KEY);
        localStorage.removeItem('srs-claimed-rewards-v1'); localStorage.removeItem('srs-reward-history-v2');
        sessionStorage.removeItem('srs-story-v1');
        window.dispatchEvent(new CustomEvent('srs-rewards-reset'));
        setMyMemberIdState(null); setSelectedTeam(null); setMembers([]);
        const fresh = defaultScoreState(); setScores(fresh); scoresRef.current = fresh;
        setTeamMottos({ black: '', white: '' });
        // 保留任務編輯覆蓋，遊戲重置以最新編輯為主
        const prevSS = scheduleStateRef.current;
        const ds = {
          ...defaultScheduleState(),
          taskOverrides: prevSS.taskOverrides,
          eventOverrides: prevSS.eventOverrides,
        };
        setScheduleState(ds); scheduleStateRef.current = ds;
        setGameStartedAt(null);
      })
      .subscribe();
    gameEventChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); gameEventChannelRef.current = null; };
  }, []);

  // ─── Ceremony Broadcast Channel ───────────────────────────────────────────
  useEffect(() => {
    const channel = supabase.channel('srs-ceremony-broadcast', { config: { broadcast: { ack: false } } });
    channel
      .on('broadcast', { event: 'ceremony_active' }, (msg) => {
        const { active, phase, index, revealed } = msg.payload as { active: boolean; phase: 'intro' | 'reveal' | 'done'; index: number; revealed: boolean };
        setScheduleState(prev => { const ns = { ...prev, ceremonyActive: active, ceremonyPhase: phase, ceremonyIndex: index, ceremonyRevealed: revealed }; scheduleStateRef.current = ns; return ns; });
      })
      .on('broadcast', { event: 'ceremony_progress' }, (msg) => {
        const { phase, index, revealed } = msg.payload as { phase: 'intro' | 'reveal' | 'done'; index: number; revealed: boolean };
        setScheduleState(prev => { const ns = { ...prev, ceremonyPhase: phase, ceremonyIndex: index, ceremonyRevealed: revealed }; scheduleStateRef.current = ns; return ns; });
      })
      .subscribe();
    ceremonyChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); ceremonyChannelRef.current = null; };
  }, []);

  // ─── Settlement Broadcast Channel ─────────────────────────────────────────
  useEffect(() => {
    const channel = supabase.channel('srs-settlement-broadcast', { config: { broadcast: { ack: false } } });
    channel
      .on('broadcast', { event: 'settlement_update' }, (msg) => {
        const { active } = msg.payload as { active: boolean };
        setScheduleState(prev => { const ns = { ...prev, settlementActive: active }; scheduleStateRef.current = ns; return ns; });
      })
      .subscribe();
    settlementChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); settlementChannelRef.current = null; };
  }, []);

  // ─── Hidden Mission Broadcast Channel ────────────────────────────────────
  useEffect(() => {
    const channel = supabase.channel('srs-hidden-mission-broadcast', { config: { broadcast: { ack: false } } });
    channel
      .on('broadcast', { event: 'hidden_mission_update' }, (msg) => {
        const { active, status, startedAt } = msg.payload as { active?: boolean; status?: 'idle' | 'ongoing' | 'done'; startedAt?: number | null };
        setScheduleState(prev => {
          const ns = {
            ...prev,
            ...(active !== undefined ? { hiddenMissionActive: active } : {}),
            ...(status !== undefined ? { hiddenMissionStatus: status } : {}),
            ...(startedAt !== undefined ? { hiddenMissionStartedAt: startedAt } : {}),
          };
          scheduleStateRef.current = ns;
          return ns;
        });
      })
      .subscribe();
    hiddenMissionChannelRef.current = channel;
    return () => { supabase.removeChannel(channel); hiddenMissionChannelRef.current = null; };
  }, []);

  // ─── Polling fallback（每 8 秒強制拉一次最新資料，防止 realtime 斷線）────
  useEffect(() => {
    const poll = async () => {
      // 如果裁判端正在寫入，跳過這次 poll 避免覆蓋
      if (suppressRealtimeScore.current || suppressRealtimeSchedule.current) return;
      const [scoresRes, scheduleRes] = await Promise.all([
        supabase.from('scores').select('*').eq('id', 'main').maybeSingle(),
        supabase.from('schedule_state').select('*').eq('id', 'main').maybeSingle(),
      ]);
      if (scoresRes.data && !suppressRealtimeScore.current) {
        const s = dbRowToScoreState(scoresRes.data as Record<string, unknown>);
        // 只有資料真的不同才更新，避免不必要的 re-render
        const cur = scoresRef.current;
        const changed =
          s.black.battle !== cur.black.battle ||
          s.black.chemistry !== cur.black.chemistry ||
          s.black.dreamCoins !== cur.black.dreamCoins ||
          s.white.battle !== cur.white.battle ||
          s.white.chemistry !== cur.white.chemistry ||
          s.white.dreamCoins !== cur.white.dreamCoins;
        if (changed) {
          setScores(s);
          scoresRef.current = s;
        }
        const gsa = (scoresRes.data as Record<string, unknown>).game_started_at;
        setGameStartedAt(typeof gsa === 'number' ? gsa : null);
      }
      if (scheduleRes.data && !suppressRealtimeSchedule.current) {
        const ss = dbRowToScheduleState(scheduleRes.data as Record<string, unknown>);
        setScheduleState(ss);
        scheduleStateRef.current = ss;
      }
    };
    const interval = setInterval(poll, 8000);
    return () => clearInterval(interval);
  }, []);

  // ─── Sys Comment Broadcast Channel ───────────────────────────────────────
  // 注意：這個 channel 只負責「發送」系統通知廣播
  // 接收端在 useComments.ts 的 srs-sys-comment-broadcast channel
  // 兩個地方不能同時訂閱同名 channel，所以 ScoreContext 只負責發送，不訂閱接收
  useEffect(() => {
    const ch = supabase.channel('srs-sys-comment-broadcast', {
      config: { broadcast: { ack: false } },
    });
    ch.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        _sysCommentBroadcast = (id, message, team, createdAt) => {
          ch.send({ type: 'broadcast', event: 'sys_comment', payload: { id, message, team, createdAt } });
        };
        // Flush any queued broadcasts that arrived before channel was ready
        while (_sysBroadcastQueue.length > 0) {
          const [id, msg, team, ts] = _sysBroadcastQueue.shift()!;
          _sysCommentBroadcast(id, msg, team, ts);
        }
      }
    });
    return () => {
      _sysCommentBroadcast = null;
      supabase.removeChannel(ch);
    };
  }, []);

  const resetGame = useCallback(async () => {
    localStorage.removeItem(MY_MEMBER_KEY); localStorage.removeItem(TEAM_KEY);
    localStorage.removeItem('srs-claimed-rewards-v1'); localStorage.removeItem('srs-reward-history-v2');
    sessionStorage.removeItem('srs-story-v1');
    window.dispatchEvent(new CustomEvent('srs-rewards-reset'));
    setMyMemberIdState(null); setSelectedTeam(null); setMembers([]);
    const fresh = defaultScoreState(); setScores(fresh); scoresRef.current = fresh;
    setTeamMottos({ black: '', white: '' });
    // 保留任務編輯覆蓋，遊戲重置以最新編輯為主
    const prevSS = scheduleStateRef.current;
    const ds = {
      ...defaultScheduleState(),
      taskOverrides: prevSS.taskOverrides,
      eventOverrides: prevSS.eventOverrides,
      // hiddenMission 完整重置
      hiddenMissionActive: false,
      hiddenMissionStatus: 'idle' as const,
      hiddenMissionStartedAt: null,
    };
    setScheduleState(ds); scheduleStateRef.current = ds;
    setGameStartedAt(null);
    // Broadcast hidden mission reset to all devices
    hiddenMissionChannelRef.current?.send({
      type: 'broadcast', event: 'hidden_mission_update',
      payload: { active: false, status: 'idle' },
    });
    // 廣播 comments_cleared 讓所有裝置立即清空留言（比等 DB DELETE realtime 快）
    // 使用 srs-comments-game-events channel（與 useComments 一致）
    await supabase.channel('srs-comments-game-events').send({
      type: 'broadcast', event: 'comments_cleared', payload: {},
    });
    suppressRealtimeScore.current = true;
    await Promise.all([
      supabase.from('members').delete().gte('joined_at', 0),
      supabase.from('comments').delete().gte('created_at', 0),
      supabase.from('lottery_items').delete().not('id', 'is', null),
      supabase.from('scores').update({ black_battle: 0, black_chemistry: 0, black_dream_coins: 0, white_battle: 0, white_chemistry: 0, white_dream_coins: 0, history: [], game_started_at: null, updated_at: new Date().toISOString() }).eq('id', 'main'),
      supabase.from('team_mottos').update({ black: '', white: '', updated_at: new Date().toISOString() }).eq('id', 'main'),
      supabase.from('schedule_state').update({
        event_statuses: {}, unlocked_tasks: {}, secret_pairings: {},
        ceremony_active: false, ceremony_phase: 'intro', ceremony_index: 0, ceremony_revealed: false,
        rating_active: false, voting_type: 'individual', voting_title: '',
        custom_tasks: [],
        // task_overrides 和 event_overrides 保留最新編輯，不重置
        task_additions: {}, task_deletions: {},
        settlement_active: false,
        hidden_mission_active: false, hidden_mission_status: 'idle', hidden_mission_started_at: null,
        updated_at: new Date().toISOString(),
      }).eq('id', 'main'),
    ]);
    setTimeout(() => { suppressRealtimeScore.current = false; }, 500);
    if (gameEventChannelRef.current) {
      await gameEventChannelRef.current.send({ type: 'broadcast', event: 'game_reset', payload: {} });
    }
  }, []);

  const resetMembers = useCallback(async () => {
    setMembers([]); localStorage.removeItem(MY_MEMBER_KEY); localStorage.removeItem(TEAM_KEY);
    setMyMemberIdState(null); setSelectedTeam(null);
    await supabase.from('members').delete().gte('joined_at', 0);
  }, []);

  const resetMottos = useCallback(async () => {
    setTeamMottos({ black: '', white: '' });
    await supabase.from('team_mottos').update({ black: '', white: '', updated_at: new Date().toISOString() }).eq('id', 'main');
  }, []);

  const resetEventStatuses = useCallback(async () => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, eventStatuses: {} };
    setScheduleState(newState); scheduleStateRef.current = newState;
    suppressRealtimeSchedule.current = true;
    await supabase.from('schedule_state').update({ event_statuses: {}, updated_at: new Date().toISOString() }).eq('id', 'main');
    setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500);
  }, []);

  const resetTaskUnlocks = useCallback(async () => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, unlockedTasks: {} };
    setScheduleState(newState); scheduleStateRef.current = newState;
    suppressRealtimeSchedule.current = true;
    await supabase.from('schedule_state').update({ unlocked_tasks: {}, updated_at: new Date().toISOString() }).eq('id', 'main');
    setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500);
  }, []);

  const resetLotteryItems = useCallback(async () => {
    await supabase.from('lottery_items').delete().not('id', 'is', null);
  }, []);

  const resetTaskCustomizations = useCallback(async () => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = {
      ...current,
      taskOverrides: {},
      eventOverrides: {},
      taskAdditions: {},
      taskDeletions: {},
      customTasks: [],
    };
    setScheduleState(newState); scheduleStateRef.current = newState;
    suppressRealtimeSchedule.current = true;
    await supabase.from('schedule_state').update({
      task_overrides: {},
      event_overrides: {},
      task_additions: {},
      task_deletions: {},
      custom_tasks: [],
      updated_at: new Date().toISOString(),
    }).eq('id', 'main');
    setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500);
  }, []);

  const startGame = useCallback(async () => {
    const now = Date.now();
    setGameStartedAt(now);

    // Broadcast for instant sync
    suppressRealtimeScore.current = true;
    scoreBroadcastRef.current?.send({
      type: 'broadcast', event: 'score_update',
      payload: { scores: scoresRef.current, gameStartedAt: now },
    });

    await supabase.from('scores').update({ game_started_at: now, updated_at: new Date().toISOString() } as Record<string, unknown>).eq('id', 'main');
    setTimeout(() => { suppressRealtimeScore.current = false; }, 1500);
    insertSysComment('⚔️ 遊戲正式開始！祝各隊旗開得勝！');
  }, []);

  // ─── Motto Ops ────────────────────────────────────────────────────────────
  const updateTeamMotto = useCallback(async (team: TeamName, motto: string) => {
    setTeamMottos(prev => ({ ...prev, [team]: motto }));
    await supabase.from('team_mottos').update({ [team]: motto, updated_at: new Date().toISOString() }).eq('id', 'main');
  }, []);

  // ─── Schedule Ops ─────────────────────────────────────────────────────────
  const setEventStatus = useCallback((key: string, status: TaskStatus) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, eventStatuses: { ...current.eventStatuses, [key]: status } };
    setScheduleState(newState); scheduleStateRef.current = newState;
    const title = getEventTitle(key);
    if (status === 'ongoing') insertSysComment(`🔥「${title}」開始了！`);
    else if (status === 'done') insertSysComment(`✅「${title}」已完成！`);

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ event_statuses: newState.eventStatuses, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const toggleTaskUnlock = useCallback((key: string) => {
    const current = scheduleStateRef.current;
    const wasUnlocked = !!current.unlockedTasks[key];
    const newState: ScheduleStateData = { ...current, unlockedTasks: { ...current.unlockedTasks, [key]: !wasUnlocked } };
    setScheduleState(newState); scheduleStateRef.current = newState;
    const taskTitle = getTaskTitle(key);
    if (!wasUnlocked) insertSysComment(`🔓 任務「${taskTitle}」已解鎖，快去查看！`);
    else insertSysComment(`🔒 任務「${taskTitle}」已重新鎖定`);

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ unlocked_tasks: newState.unlockedTasks, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const setSecretPairings = useCallback((pairings: Record<string, string>) => {
    const current = scheduleStateRef.current;
    // 清除配對時，同步重置揭曉特效的 unlock 狀態，避免玩家個人頁仍顯示任務卡片
    const newUnlockedTasks = Object.keys(pairings).length === 0
      ? Object.fromEntries(Object.entries(current.unlockedTasks).filter(([k]) => k !== MISSION_TASK_KEY))
      : current.unlockedTasks;
    const newState: ScheduleStateData = { ...current, secretPairings: pairings, unlockedTasks: newUnlockedTasks };
    setScheduleState(newState); scheduleStateRef.current = newState;
    if (Object.keys(pairings).length > 0) insertSysComment('✦ 小主人配對已啟動，任務開始！');

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ secret_pairings: pairings, unlocked_tasks: newUnlockedTasks, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  // ─── Ceremony Ops (Broadcast + DB) ────────────────────────────────────────
  const setCeremonyActive = useCallback((active: boolean) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = {
      ...current,
      ceremonyActive: active,
      ...(active ? { ceremonyPhase: 'intro' as const, ceremonyIndex: 0, ceremonyRevealed: false } : {}),
    };
    setScheduleState(newState); scheduleStateRef.current = newState;
    if (active) insertSysComment('🎭 小天使揭曉儀式正式開始！快打開畫面一起見證！');

    // Broadcast for instant sync
    ceremonyChannelRef.current?.send({
      type: 'broadcast', event: 'ceremony_active',
      payload: { active, phase: active ? 'intro' : newState.ceremonyPhase, index: active ? 0 : newState.ceremonyIndex, revealed: active ? false : newState.ceremonyRevealed },
    });

    // DB for persistence
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state').update({
      ceremony_active: active,
      ...(active ? { ceremony_phase: 'intro', ceremony_index: 0, ceremony_revealed: false } : {}),
      updated_at: new Date().toISOString(),
    }).eq('id', 'main').then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  const setCeremonyProgress = useCallback((phase: 'intro' | 'reveal' | 'done', index: number, revealed: boolean) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, ceremonyPhase: phase, ceremonyIndex: index, ceremonyRevealed: revealed };
    setScheduleState(newState); scheduleStateRef.current = newState;

    // Broadcast for instant sync
    ceremonyChannelRef.current?.send({
      type: 'broadcast', event: 'ceremony_progress',
      payload: { phase, index, revealed },
    });

    // DB for persistence
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state').update({ ceremony_phase: phase, ceremony_index: index, ceremony_revealed: revealed, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  // ─── Rating Ops ───────────────────────────────────────────────────────────
  const setRatingActive = useCallback((active: boolean) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, ratingActive: active };
    setScheduleState(newState); scheduleStateRef.current = newState;
    if (active) {
      const modeLabel = current.votingType === 'team' ? '隊伍評分' : '個人評分';
      const titlePart = current.votingTitle ? `「${current.votingTitle}」` : '';
      insertSysComment(`⭐ ${titlePart}${modeLabel}已開啟！快到導覽列為隊伍評分！`);
    }
    // Broadcast for instant sync（讓玩家端立即看到評分按鈕，不用等 8 秒 polling）
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });
    supabase.from('schedule_state').update({ rating_active: active, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const setVotingConfig = useCallback((type: 'team' | 'individual', title: string) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, votingType: type, votingTitle: title };
    setScheduleState(newState); scheduleStateRef.current = newState;
    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });
    supabase.from('schedule_state').update({ voting_type: type, voting_title: title, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  // ─── Custom Task Ops ──────────────────────────────────────────────────────
  const addCustomTask = useCallback((task: Omit<CustomTask, 'id' | 'createdAt'>) => {
    const newTask: CustomTask = {
      ...task,
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      createdAt: Date.now(),
      status: 'ongoing',
    };
    const current = scheduleStateRef.current;
    const newCustomTasks = [...current.customTasks, newTask];
    const newState: ScheduleStateData = { ...current, customTasks: newCustomTasks };
    setScheduleState(newState); scheduleStateRef.current = newState;
    insertSysComment(`📋 裁判新增了臨時任務：「${newTask.title}」，快去冒險旅程查看！`);

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ custom_tasks: newCustomTasks, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const removeCustomTask = useCallback((id: string) => {
    const current = scheduleStateRef.current;
    const newCustomTasks = current.customTasks.filter(t => t.id !== id);
    const newState: ScheduleStateData = { ...current, customTasks: newCustomTasks };
    setScheduleState(newState); scheduleStateRef.current = newState;

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ custom_tasks: newCustomTasks, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const setCustomTaskStatus = useCallback((id: string, status: TaskStatus) => {
    const current = scheduleStateRef.current;
    const task = current.customTasks.find(t => t.id === id);
    if (!task) return;
    const newCustomTasks = current.customTasks.map(t => t.id === id ? { ...t, status } : t);
    const newState: ScheduleStateData = { ...current, customTasks: newCustomTasks };
    setScheduleState(newState); scheduleStateRef.current = newState;
    if (status === 'done') insertSysComment(`✅ 裁判臨時任務「${task.title}」已完成！玩家可領取獎勵！`);
    else if (status === 'ongoing') insertSysComment(`🔥 裁判臨時任務「${task.title}」已開始！`);

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ custom_tasks: newCustomTasks, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  // ─── Task Override Ops ────────────────────────────────────────────────────
  const updateTaskOverride = useCallback((key: string, override: { name: string; desc: string }) => {
    const current = scheduleStateRef.current;
    const newOverrides = { ...current.taskOverrides, [key]: override };
    const newState: ScheduleStateData = { ...current, taskOverrides: newOverrides };
    setScheduleState(newState); scheduleStateRef.current = newState;
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state').update({ task_overrides: newOverrides, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  // ─── Event Override Ops ───────────────────────────────────────────────────
  const updateEventOverride = useCallback((key: string, override: { title: string; time: string }) => {
    const current = scheduleStateRef.current;
    const newOverrides = { ...current.eventOverrides, [key]: override };
    const newState: ScheduleStateData = { ...current, eventOverrides: newOverrides };
    setScheduleState(newState); scheduleStateRef.current = newState;
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state').update({ event_overrides: newOverrides, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  // ─── Start Event + Bulk Unlock (atomic) ───────────────────────────────────
  const startEventAndUnlockTasks = useCallback((dayId: string, eventIdx: number) => {
    const current = scheduleStateRef.current;
    const day = scheduleDays.find(d => d.id === dayId);
    if (!day || !day.events[eventIdx]) return;
    const ev = day.events[eventIdx];

    // Bulk-unlock all tasks in this event
    const newUnlockedTasks = { ...current.unlockedTasks };
    ev.tasks.forEach((_, ti) => {
      newUnlockedTasks[buildTaskKey(dayId, eventIdx, ti)] = true;
    });

    // Set event status to ongoing
    const eventKey = buildEventKey(dayId, eventIdx);
    const newEventStatuses = { ...current.eventStatuses, [eventKey]: 'ongoing' as TaskStatus };

    // Atomic state update
    const newState: ScheduleStateData = {
      ...current,
      eventStatuses: newEventStatuses,
      unlockedTasks: newUnlockedTasks,
    };
    setScheduleState(newState);
    scheduleStateRef.current = newState;

    // Single system comment
    insertSysComment(`🔥「${ev.title}」開始了！任務已解鎖，快去查看！`);

    // Broadcast for instant sync to all devices
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    // Single DB write
    supabase.from('schedule_state').update({
      event_statuses: newEventStatuses,
      unlocked_tasks: newUnlockedTasks,
      updated_at: new Date().toISOString(),
    }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  // ─── Task Addition Ops ────────────────────────────────────────────────────
  const addTaskToEvent = useCallback((dayId: string, eventIdx: number, task: { badge: string; badgeColor: string; name: string; desc: string }) => {
    const current = scheduleStateRef.current;
    const eventKey = buildEventKey(dayId, eventIdx);
    const newTask = { ...task, id: `added-${eventKey}-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    const existing = current.taskAdditions[eventKey] ?? [];
    const newAdditions = { ...current.taskAdditions, [eventKey]: [...existing, newTask] };
    const newState: ScheduleStateData = { ...current, taskAdditions: newAdditions };
    setScheduleState(newState); scheduleStateRef.current = newState;
    insertSysComment(`📋 裁判為活動新增了任務：「${newTask.name}」`);

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ task_additions: newAdditions, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const deleteTask = useCallback((key: string) => {
    const current = scheduleStateRef.current;
    let newAdditions = current.taskAdditions;
    let newDeletions = current.taskDeletions;

    if (key.startsWith('added-')) {
      // Remove from additions
      newAdditions = Object.fromEntries(
        Object.entries(current.taskAdditions).map(([evKey, tasks]) => [
          evKey,
          tasks.filter(t => t.id !== key),
        ])
      );
    } else {
      // Mark original task as deleted
      newDeletions = { ...current.taskDeletions, [key]: true };
    }

    const newState: ScheduleStateData = { ...current, taskAdditions: newAdditions, taskDeletions: newDeletions };
    setScheduleState(newState); scheduleStateRef.current = newState;

    // Broadcast for instant sync
    suppressRealtimeSchedule.current = true;
    scheduleBroadcastRef.current?.send({
      type: 'broadcast', event: 'schedule_update',
      payload: newState,
    });

    supabase.from('schedule_state').update({ task_additions: newAdditions, task_deletions: newDeletions, updated_at: new Date().toISOString() }).eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 1500); });
  }, []);

  const currentMember = members.find(m => m.id === myMemberId) ?? null;

  const resetAllTasks = useCallback(async () => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = {
      ...current,
      eventStatuses: {},
      unlockedTasks: {},
      customTasks: [],
      // 保留任務編輯覆蓋，以最新編輯為主
      taskAdditions: {},
      taskDeletions: {},
    };
    setScheduleState(newState);
    scheduleStateRef.current = newState;

    // 清除本地獎勵領取紀錄，並廣播給同裝置的玩家頁面
    localStorage.removeItem('srs-claimed-rewards-v1');
    localStorage.removeItem('srs-reward-history-v2');
    window.dispatchEvent(new CustomEvent('srs-rewards-reset'));

    suppressRealtimeSchedule.current = true;
    await supabase.from('schedule_state').update({
      event_statuses: {},
      unlocked_tasks: {},
      custom_tasks: [],
      // task_overrides 和 event_overrides 保留最新編輯，不重置
      task_additions: {},
      task_deletions: {},
      updated_at: new Date().toISOString(),
    }).eq('id', 'main');
    setTimeout(() => { suppressRealtimeSchedule.current = false; }, 800);
  }, []);

  // ─── Hidden Mission Ops ───────────────────────────────────────────────────
  const setHiddenMissionActive = useCallback((active: boolean) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, hiddenMissionActive: active };
    setScheduleState(newState);
    scheduleStateRef.current = newState;

    if (active) insertSysComment(`🔮 隱藏任務「微光記憶：民宿密令」已解鎖！快打開畫面查看！`);

    // Broadcast for instant sync
    hiddenMissionChannelRef.current?.send({
      type: 'broadcast',
      event: 'hidden_mission_update',
      payload: { active, status: active ? 'idle' : 'idle' },
    });

    // DB for persistence
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state')
      .update({ hidden_mission_active: active, hidden_mission_status: 'idle', updated_at: new Date().toISOString() })
      .eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  const setHiddenMissionStatus = useCallback((status: 'idle' | 'ongoing' | 'done') => {
    const current = scheduleStateRef.current;
    const startedAt = status === 'ongoing' ? Date.now() : (status === 'idle' ? null : current.hiddenMissionStartedAt);
    const newState: ScheduleStateData = { ...current, hiddenMissionStatus: status, hiddenMissionStartedAt: startedAt };
    setScheduleState(newState);
    scheduleStateRef.current = newState;

    if (status === 'ongoing') insertSysComment(`🔥 隱藏任務「微光記憶：民宿密令」開始！40 分鐘內蒐集所有微光碎片！`);
    else if (status === 'done') insertSysComment(`✅ 隱藏任務「微光記憶：民宿密令」已結束！玩家可領取獎勵！`);

    hiddenMissionChannelRef.current?.send({
      type: 'broadcast',
      event: 'hidden_mission_update',
      payload: { active: current.hiddenMissionActive, status, startedAt },
    });

    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state')
      .update({ hidden_mission_status: status, hidden_mission_started_at: startedAt, updated_at: new Date().toISOString() })
      .eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  // ─── Settlement Ops ───────────────────────────────────────────────────────
  const setSettlementActive = useCallback((active: boolean) => {
    const current = scheduleStateRef.current;
    const newState: ScheduleStateData = { ...current, settlementActive: active };
    setScheduleState(newState);
    scheduleStateRef.current = newState;

    // Broadcast for instant sync to all devices
    settlementChannelRef.current?.send({
      type: 'broadcast',
      event: 'settlement_update',
      payload: { active },
    });

    // DB for persistence
    suppressRealtimeSchedule.current = true;
    supabase.from('schedule_state')
      .update({ settlement_active: active, updated_at: new Date().toISOString() })
      .eq('id', 'main')
      .then(() => { setTimeout(() => { suppressRealtimeSchedule.current = false; }, 500); });
  }, []);

  return (
    <ScoreContext.Provider value={{
      scores, selectedTeam, selectTeam, addScore, resetScores, resetGame, resetMembers, resetMottos,
      resetEventStatuses, resetTaskUnlocks, resetLotteryItems, resetTaskCustomizations, resetAllTasks,
      totalBlack: getTotal(scores.black), totalWhite: getTotal(scores.white),
      showTeamModal, setShowTeamModal, showJudgePanel, setShowJudgePanel,
      members, currentMember, addOrUpdateMember, updateMember, updateMemberTeam, updateMemberAvatar, removeMember, transferMemberRecord,
      myMemberId, setMyMemberId, showMemberModal, setShowMemberModal, pendingTeam, setPendingTeam,
      teamMottos, updateTeamMotto, isLoading, scheduleState,
      setEventStatus, toggleTaskUnlock, setSecretPairings,
      setCeremonyActive, setCeremonyProgress, setRatingActive,
      setVotingConfig, setSettlementActive, setHiddenMissionActive, setHiddenMissionStatus,
      gameStartedAt, startGame,
      addCustomTask, removeCustomTask, setCustomTaskStatus,
      updateTaskOverride, updateEventOverride,
      startEventAndUnlockTasks,
      addTaskToEvent, deleteTask,
    }}>
      {children}
    </ScoreContext.Provider>
  );
}

export function useScore() {
  const ctx = useContext(ScoreContext);
  if (!ctx) throw new Error('useScore must be used within ScoreProvider');
  return ctx;
}
